import React, { useMemo } from 'react';
import { Table, Badge, Form } from 'react-bootstrap';
import { getFieldLabel } from '../../utils/fieldLabels';
import { ACTION_BADGE, formatDateTime } from './historyUtils';
import useSortFilterSearch from './useSortFilterSearch';
import { ControlsBar, MultiFilter, ResultCount, Th } from './TableControls';
import { PhotoChangeCell } from './PhotoViewerModal';

const BASE_SEARCH_FNS = [
  r => r.asset_label || r.asset_guid,
  r => r.changed_by,
  r => ACTION_BADGE[r.action]?.label || r.action,
  r => r.field_name ? getFieldLabel(r.field_name) : '',
  r => formatDateTime(r.changed_at),
];
const FLOOR_SEARCH_FN = r => r.floor_label;

function resolveAssetFieldLabel(fieldName, assetFieldLabelMap) {
  if (!fieldName) return '—';
  if (assetFieldLabelMap && assetFieldLabelMap[fieldName]) return assetFieldLabelMap[fieldName];
  return fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function AssetChangesTab({ records, onViewPhotos, assetFieldLabelMap, isMultiFloor, floorOptions, selectedFloor, onFloorChange }) {
  const showFloorColumn = isMultiFloor && !selectedFloor;

  const searchFns = useMemo(
    () => isMultiFloor ? [...BASE_SEARCH_FNS, FLOOR_SEARCH_FN] : BASE_SEARCH_FNS,
    [isMultiFloor]
  );

  const normalizedRecords = useMemo(
    () => records.map(r => ({ ...r, changed_by: r.changed_by_display_name || r.changed_by_username || '' })),
    [records]
  );

  const userOptions = useMemo(() =>
    [...new Set(normalizedRecords.map(r => r.changed_by).filter(Boolean))]
      .sort().map(v => ({ value: v, label: v })),
    [normalizedRecords]
  );

  const actionOptions = useMemo(() =>
    [...new Set(records.map(r => r.action).filter(Boolean))]
      .sort().map(v => ({ value: v, label: ACTION_BADGE[v]?.label || v })),
    [records]
  );

  const { processed, sortConfig, toggleSort, filters, setFilter, search, setSearch } =
    useSortFilterSearch(normalizedRecords, searchFns);

  if (!records.length) {
    return <p className="text-muted mt-3">No asset changes recorded.</p>;
  }

  const colSpan = showFloorColumn ? 7 : 6;

  return (
    <>
      <ControlsBar search={search} onSearch={setSearch}>
        {isMultiFloor && (
          <div style={{ minWidth: 130, maxWidth: 180 }}>
            <Form.Control
              as="select"
              size="sm"
              style={{ fontSize: '0.875rem', border: '1px solid #ced4da' }}
              value={selectedFloor}
              onChange={e => onFloorChange(e.target.value)}
            >
              <option value="">All Floors</option>
              {floorOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Control>
          </div>
        )}
        <div style={{ minWidth: 180, maxWidth: 220 }}>
          <MultiFilter
            placeholder="All users"
            value={filters.changed_by}
            options={userOptions}
            onChange={v => setFilter('changed_by', v)}
          />
        </div>
        <div style={{ minWidth: 160, maxWidth: 220 }}>
          <MultiFilter
            placeholder="All actions"
            value={filters.action}
            options={actionOptions}
            onChange={v => setFilter('action', v)}
          />
        </div>
        <ResultCount total={records.length} filtered={processed.length} />
      </ControlsBar>

      <Table striped hover responsive size="sm">
        <thead>
          <tr>
            {showFloorColumn && <Th col="floor_label" label="Floor"      sortConfig={sortConfig} onSort={toggleSort} />}
            <Th col="asset_label" label="Asset"        sortConfig={sortConfig} onSort={toggleSort} />
            <Th col="changed_by"  label="User"         sortConfig={sortConfig} onSort={toggleSort} />
            <Th col="action"      label="Action"       sortConfig={sortConfig} onSort={toggleSort} />
            <Th col="field_name"  label="Field"        sortConfig={sortConfig} onSort={toggleSort} />
            <th>Change</th>
            <Th col="changed_at"  label="Date / Time"  sortConfig={sortConfig} onSort={toggleSort} />
          </tr>
        </thead>
        <tbody>
          {processed.length === 0 ? (
            <tr><td colSpan={colSpan} className="text-center text-muted">No results match your filters.</td></tr>
          ) : (
            processed.map(r => {
              const badge = ACTION_BADGE[r.action] || { bg: 'secondary', label: r.action };
              const showFieldAndChange = r.action === 'FIELD_UPDATE' || r.action === 'PHOTO_UPDATE';
              return (
                <tr key={r.id}>
                  {showFloorColumn && <td style={{ whiteSpace: 'nowrap' }}>{r.floor_label || '—'}</td>}
                  <td>{r.asset_label || r.asset_guid || '—'}</td>
                  <td>{r.changed_by}</td>
                  <td><Badge bg={badge.bg}>{badge.label}</Badge></td>
                  <td title={showFieldAndChange ? r.field_name : undefined}>
                    {showFieldAndChange ? resolveAssetFieldLabel(r.field_name, assetFieldLabelMap) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {showFieldAndChange
                      ? <PhotoChangeCell oldValue={r.old_value} newValue={r.new_value} onViewPhotos={onViewPhotos} />
                      : <span className="text-muted">—</span>
                    }
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.changed_at)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </>
  );
}
